import React from 'react'

import macbookModalImg from '@/assets/images/macbook-modal-img.png'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

const SUPPORT_WHATSAPP_URL = 'https://wa.me/15557052438'

interface AccountSuspendedModalProps {
	visible: boolean
	/** 'suspended' (default): paying customer blocked — non-dismissible, WhatsApp CTA.
	 *  'trial': trial expired inbound user — dismissible, "Choose a plan" CTA. */
	variant?: 'suspended' | 'trial'
	/** Required when variant is 'trial' to handle modal dismissal. */
	onDismiss?: () => void
}

export const AccountSuspendedModal: React.FC<AccountSuspendedModalProps> = ({
	visible,
	variant = 'suspended',
	onDismiss,
}) => {
	const { t } = useTranslation('common')

	const isTrial = variant === 'trial'
	const titleKey = isTrial
		? 'account.trial_expired_modal.title'
		: 'account.suspended_modal.title'
	const descriptionKey = isTrial
		? 'account.trial_expired_modal.description'
		: 'account.suspended_modal.description'

	return (
		<Dialog open={visible}>
			<DialogContent
				className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
				onInteractOutside={(e) => e.preventDefault()}
				onEscapeKeyDown={(e) => e.preventDefault()}
			>
				<div className="px-8 py-12 flex flex-col items-center text-center space-y-6">
					{/* Image */}
					<div className="w-full flex justify-center">
						<img
							src={macbookModalImg}
							alt="Brandtrack App"
							className="w-72 h-auto object-contain"
						/>
					</div>

					{/* Title */}
					<DialogTitle className="text-3xl font-normal">
						{t(titleKey)}
					</DialogTitle>

					{/* Description */}
					<DialogDescription className="text-base text-muted-foreground max-w-md">
						{t(descriptionKey)}
					</DialogDescription>

					{/* CTA */}
					<div className="pt-4">
						{isTrial ? (
							<Button
								onClick={onDismiss}
								className="w-full sm:w-auto min-w-[200px] h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
							>
								{t('account.trial_expired_modal.select_plan')}
							</Button>
						) : (
							<Button
								onClick={() =>
									window.open(
										SUPPORT_WHATSAPP_URL,
										'_blank',
										'noopener,noreferrer'
									)
								}
								className="w-full sm:w-auto min-w-[200px] h-12 text-base font-medium rounded-full bg-white text-black hover:bg-gray-200"
							>
								{t('account.suspended_modal.contact_us')}
							</Button>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default AccountSuspendedModal
